<template>
  <div class="video-frame-container" :style="containerStyle">
    <BaseVideo
      ref="videoComponentRef"
      :src="props.src"
      @timeupdate="handleTimeUpdate"
      @loadedmetadata="handleMetadata"
    />
    <div
      v-for="overlay in props.config"
      :key="overlay.id"
      :class="[
        'text',
        overlay.styleClass,
        getTransitionClass(overlay),
        { active: isOverlayActive(overlay) },
      ]"
      :style="getOverlayStyle(overlay)"
      v-html="overlay.content"
    />
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineExpose } from 'vue'
import BaseVideo from './BaseVideo.vue'

const props = defineProps({
  src: { type: String, required: true },
  config: { type: Array, default: () => [] },
})

const videoComponentRef = ref(null)
const currentTime = ref(0)
const aspectRatio = ref(16 / 9) // Default aspect ratio

const containerStyle = computed(() => ({
  paddingTop: `${(1 / aspectRatio.value) * 100}%`,
}))

const handleTimeUpdate = event => {
  currentTime.value = event.target.currentTime
}

const handleMetadata = videoEl => {
  if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
    aspectRatio.value = videoEl.videoWidth / videoEl.videoHeight
  }
}

const isOverlayActive = overlay => {
  return currentTime.value >= overlay.start && currentTime.value < overlay.end
}

const getOverlayStyle = overlay => ({
  top: `${overlay.position.top}%`,
  left: `${overlay.position.left}%`,
})

const getTransitionClass = overlay => {
  const type = overlay.transition?.type || 'fade'
  const direction = overlay.transition?.direction || ''
  return type === 'slide'
    ? `transition-slide-${direction}`
    : `transition-${type}`
}

const play = () => videoComponentRef.value?.play()
const pause = () => videoComponentRef.value?.pause()
const seek = time => videoComponentRef.value?.seek(time)

defineExpose({ play, pause, seek })
</script>

<style scoped>
.video-frame-container {
  position: relative;
  width: 100%;
  background-color: #000;
  overflow: hidden;
}

.video-frame-container > :deep(video) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.text {
  position: absolute;
  color: white;
  font-family: var(--sans-serif);
  padding: 12px 24px;
  border-radius: 8px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.text.active {
  opacity: 1;
}

/* Transitions */
.transition-fade {
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}
.transition-fade.active {
  opacity: 1;
}

.transition-zoom {
  transform: translate(-50%, -50%) scale(0.8);
  opacity: 0;
  transition: transform 0.5s ease, opacity 0.5s ease;
}
.transition-zoom.active {
  transform: translate(-50%, -50%) scale(1);
  opacity: 1;
}

.transition-slide-top {
  transform: translate(-50%, -150%);
}
.transition-slide-bottom {
  transform: translate(-50%, 50%);
}
.transition-slide-left {
  transform: translate(-150%, -50%);
}
.transition-slide-right {
  transform: translate(50%, -50%);
}

.transition-slide-top,
.transition-slide-bottom,
.transition-slide-left,
.transition-slide-right {
  opacity: 0;
  transition: transform 0.5s ease, opacity 0.5s ease;
}

.transition-slide-top.active,
.transition-slide-bottom.active,
.transition-slide-left.active,
.transition-slide-right.active {
  transform: translate(-50%, -50%);
  opacity: 1;
}
</style>
